/**
 * Fase 4G (task #391, 2026-07-07)
 *
 * "Bla og velg"-modal for NDLA-samlesider. Kurator bla i alle artikler
 * under en samleside, filtrere per emne, se lisens, velge pensum-artikler
 * og legge dem til studieplanen.
 */

import { useEffect, useState } from 'react';
import { X, Search, ChevronDown, ChevronRight, Check, Loader2, BookOpen, ExternalLink, AlertCircle } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

interface Resource {
  id: string;
  title: string;
  articleId: string;
  contentType: string;
  url: string;
  external_id: string;
  already_added: boolean;
}

interface TopicGroup {
  topic: { id: string; name: string };
  resources: Resource[];
}

interface CollectionInfo {
  title: string;
  url: string;
  total_topics: number;
  total_resources: number;
}

interface Props {
  linkId: string;
  isOpen: boolean;
  onClose: () => void;
  onArticlesAdded: (opts: { count: number; updated_at?: string | null }) => void;
}

export default function CollectionBrowserModal({ linkId, isOpen, onClose, onArticlesAdded }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [collection, setCollection] = useState<CollectionInfo | null>(null);
  const [groups, setGroups] = useState<TopicGroup[]>([]);
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState<{ added: number; skipped: number; failed: number } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setLoading(true);
    setSelectedIds(new Set());
    setAddResult(null);
    (async () => {
      try {
        const res = await authenticatedFetch('/api/literature/collection/browse', {
          method: 'POST',
          body: JSON.stringify({ link_id: linkId }),
        });
        const data = await res.json();
        if (data.success) {
          setCollection(data.collection);
          setGroups(data.groups);
          // Ekspander første emne automatisk
          if (data.groups.length > 0) {
            setExpandedTopics(new Set([data.groups[0].topic.id]));
          }
        } else {
          setError(data.error || 'Kunne ikke hente innhold');
        }
      } catch (e: any) {
        setError(e.message || 'Nettverksfeil');
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, linkId]);

  const toggleTopic = (id: string) => {
    setExpandedTopics(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleArticle = (externalId: string) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(externalId)) n.delete(externalId); else n.add(externalId);
      return n;
    });
  };

  const toggleTopicSelection = (topic: TopicGroup) => {
    const selectableIds = topic.resources.filter(r => !r.already_added).map(r => r.external_id);
    const allSelected = selectableIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (allSelected) {
        selectableIds.forEach(id => n.delete(id));
      } else {
        selectableIds.forEach(id => n.add(id));
      }
      return n;
    });
  };

  const expandAll = () => setExpandedTopics(new Set(groups.map(g => g.topic.id)));
  const collapseAll = () => setExpandedTopics(new Set());

  const filteredGroups = searchQuery.trim()
    ? groups.map(g => ({
        ...g,
        resources: g.resources.filter(r =>
          r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          g.topic.name.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter(g => g.resources.length > 0)
    : groups;

  const handleAdd = async () => {
    if (selectedIds.size === 0) return;
    setAdding(true);
    setError('');
    const articles = groups.flatMap(g =>
      g.resources
        .filter(r => selectedIds.has(r.external_id))
        .map(r => ({
          external_id: r.external_id,
          title: r.title,
          url: r.url,
          // Task #395: send emne-info så backend kan gruppere ved rendering
          topic_name: g.topic.name,
          topic_id: g.topic.id,
        }))
    );
    try {
      const res = await authenticatedFetch('/api/literature/collection/add-selected', {
        method: 'POST',
        body: JSON.stringify({ link_id: linkId, articles }),
      });
      const data = await res.json();
      if (data.success) {
        setAddResult({
          added: data.added_count,
          skipped: data.skipped_count,
          failed: data.failed_count,
        });
        onArticlesAdded({ count: data.added_count, updated_at: data.updated_at });
        // Behold modal åpen så bruker ser resultat, la den lukke selv
      } else {
        setError(data.error || 'Kunne ikke legge til artikler');
      }
    } catch (e: any) {
      setError(e.message || 'Nettverksfeil');
    } finally {
      setAdding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-kf-primary mb-1">
              <BookOpen size={22} />
              <h2 className="text-xl font-semibold">Bla i samleside og velg pensum</h2>
            </div>
            {collection && (
              <div className="text-sm text-kf-primary-70 mt-1">
                <div className="font-medium">{collection.title}</div>
                <div>
                  {collection.total_resources} artikler i {collection.total_topics} emner ·
                  <a href={collection.url} target="_blank" rel="noopener noreferrer" className="text-kf-primary hover:underline inline-flex items-center gap-1 ml-1">
                    Åpne i NDLA <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-kf-primary-60 hover:text-kf-primary flex-shrink-0">
            <X size={22} />
          </button>
        </div>

        <div className="p-4 border-b bg-kf-light flex items-center gap-3">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-kf-primary-60" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Filtrer artikler eller emner..."
              className="w-full pl-9 pr-3 py-2 border border-kf-primary-40 rounded focus:border-kf-primary focus:outline-none text-sm"
            />
          </div>
          <button onClick={expandAll} className="text-xs px-3 py-1.5 text-kf-primary hover:bg-white rounded">
            Utvid alle
          </button>
          <button onClick={collapseAll} className="text-xs px-3 py-1.5 text-kf-primary hover:bg-white rounded">
            Skjul alle
          </button>
        </div>

        <div className="p-4 border-b bg-kf-accent-10 text-sm text-kf-primary flex items-start gap-2">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            Alle NDLA-artikler har lisens <strong>CC BY-SA 4.0</strong> (fri, tillater
            arkivering og bearbeiding med kildeangivelse). Valgte artikler blir automatisk
            arkivert lokalt slik at de kan brukes til spørsmålsgenerering.
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="text-center py-10">
              <Loader2 className="animate-spin text-kf-primary mx-auto mb-4" size={36} />
              <p className="text-kf-primary-80">Henter innhold fra NDLA...</p>
            </div>
          )}

          {error && (
            <div className="bg-kf-error/10 border border-kf-error/40 rounded p-4 mb-4 text-kf-error text-sm">
              {error}
            </div>
          )}

          {addResult && (
            <div className="bg-kf-success/10 border border-kf-success/40 rounded p-4 mb-4">
              <div className="font-medium text-kf-success mb-1">
                Ferdig: {addResult.added} artikler lagt til
              </div>
              {addResult.skipped > 0 && (
                <div className="text-sm text-kf-primary-80">
                  {addResult.skipped} hoppet over (var allerede lagt til)
                </div>
              )}
              {addResult.failed > 0 && (
                <div className="text-sm text-kf-error">
                  {addResult.failed} feilet
                </div>
              )}
              <div className="text-sm text-kf-primary-80 mt-2">
                Artiklene arkiveres i bakgrunnen og blir tilgjengelige for
                spørsmålsgenerering. Du kan lukke denne dialogen.
              </div>
            </div>
          )}

          {!loading && !addResult && filteredGroups.map(group => {
            const expanded = expandedTopics.has(group.topic.id);
            const selectableCount = group.resources.filter(r => !r.already_added).length;
            const selectedInTopic = group.resources.filter(r => selectedIds.has(r.external_id)).length;
            const allSelected = selectableCount > 0 && selectedInTopic === selectableCount;
            return (
              <div key={group.topic.id} className="mb-2 border border-kf-primary-20 rounded overflow-hidden">
                <div className="flex items-center bg-kf-light">
                  <button
                    onClick={() => toggleTopic(group.topic.id)}
                    className="flex items-center gap-2 flex-1 p-3 text-left hover:bg-kf-primary-10 transition-colors"
                  >
                    {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <span className="font-medium text-kf-primary">{group.topic.name}</span>
                    <span className="text-xs text-kf-primary-60">
                      ({group.resources.length} artikler{selectedInTopic > 0 && `, ${selectedInTopic} valgt`})
                    </span>
                  </button>
                  {selectableCount > 0 && (
                    <button
                      onClick={() => toggleTopicSelection(group)}
                      className="text-xs px-3 py-1 mr-2 text-kf-primary hover:bg-kf-primary-10 rounded"
                      title={allSelected ? 'Fjern alle valg i dette emnet' : 'Velg alle artikler i dette emnet'}
                    >
                      {allSelected ? 'Fjern valg' : 'Velg alle'}
                    </button>
                  )}
                </div>
                {expanded && (
                  <div className="divide-y">
                    {group.resources.map(r => {
                      const isSelected = selectedIds.has(r.external_id);
                      const isAdded = r.already_added;
                      return (
                        <label
                          key={r.external_id}
                          className={`flex items-center gap-3 p-3 hover:bg-kf-light cursor-pointer ${isAdded ? 'opacity-60' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isAdded || isSelected}
                            disabled={isAdded}
                            onChange={() => toggleArticle(r.external_id)}
                            className="w-4 h-4 rounded border-kf-primary-40 text-kf-primary focus:ring-kf-primary"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-kf-primary truncate">{r.title}</div>
                            {isAdded && (
                              <div className="text-xs text-kf-success flex items-center gap-1 mt-0.5">
                                <Check size={10} /> Allerede lagt til
                              </div>
                            )}
                          </div>
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="text-kf-primary-60 hover:text-kf-primary flex-shrink-0"
                            title="Åpne artikkel i NDLA"
                          >
                            <ExternalLink size={14} />
                          </a>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {!loading && !addResult && filteredGroups.length === 0 && !error && (
            <div className="text-center py-10 text-kf-primary-70">
              Ingen artikler matcher søket
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-kf-light flex items-center justify-between">
          <div className="text-sm text-kf-primary">
            {addResult ? (
              <span className="text-kf-success font-medium">Ferdig</span>
            ) : (
              <span>
                <strong>{selectedIds.size}</strong> artikler valgt
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-kf-primary hover:bg-kf-primary-10 rounded"
            >
              {addResult ? 'Lukk' : 'Avbryt'}
            </button>
            {!addResult && (
              <button
                onClick={handleAdd}
                disabled={selectedIds.size === 0 || adding}
                className="bg-kf-primary text-white px-6 py-2 rounded hover:bg-kf-dark disabled:opacity-50 inline-flex items-center gap-2"
              >
                {adding ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                Legg til {selectedIds.size > 0 && `${selectedIds.size}`} artikler
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
